import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useListTenantRolesQuery,
  useListSystemRolesQuery,
  useCreateTenantRoleMutation,
  useUpdateTenantRoleMutation,
} from '@/redux/internal-services/onboardingApi';
import type { StepHandle, SubmitResult } from './stepHandle';

interface CustomRoleState {
  id: string;
  tenantRoleId?: string;
  name: string;
  mappings: string[];
  dropdownOpen: boolean;
  visibility: boolean;
}

export interface RolesStateSnapshot {
  defaultRoles: { name: string; visible: boolean }[];
  customRoles: { name: string; visible: boolean; mappings: string[] }[];
}

interface Props {
  onBack: () => void;
  onNext: () => void;
  onSkip?: () => void;
  /** Called whenever the user changes any role state (toggle, add, delete, rename, remap). */
  onRolesChange?: (state: RolesStateSnapshot) => void;
  disabled?: boolean;
}

let _seq = 0;
const uid = () => `cr_${++_seq}`;

// ── Shared checkbox button ────────────────────────────────────────────────────
const Checkbox: React.FC<{ checked: boolean; onClick: () => void; disabled?: boolean }> = ({ checked, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-checked={checked}
    role="checkbox"
    className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-dark/30 disabled:opacity-40 disabled:cursor-not-allowed"
    style={{
      backgroundColor: checked ? 'rgb(var(--color-brand-dark))' : 'transparent',
      borderColor: checked ? 'rgb(var(--color-brand-dark))' : '#9CA3AF',
      cursor: disabled ? 'not-allowed' : 'pointer',
    }}
  >
    {checked && (
      <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3">
        <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </button>
);

const TeamRolesStep = React.forwardRef<StepHandle, Props>(({ onBack, onNext, onSkip, onRolesChange, disabled = false }, ref) => {
  const { t } = useTranslation();
  const { data: tenantRolesData, isLoading } = useListTenantRolesQuery();
  const { data: systemRolesData } = useListSystemRolesQuery();
  const [createTenantRole] = useCreateTenantRoleMutation();
  const [updateTenantRole] = useUpdateTenantRoleMutation();

  const allTenantRoles = tenantRolesData?.data ?? [];
  const defaultRoles = allTenantRoles.filter((r) => r.is_default);

  // System role's canonical name (e.g. "Tenant Admin"), keyed by role_id, fetched from the DB.
  // The MAP column always shows this — it reflects what the role is actually wired to and must
  // NOT change when the tenant edits the Profile Name (defaultNames override) for the same row.
  const systemRoleNameById: Record<string, string> = {};
  for (const sr of systemRolesData?.data ?? []) systemRoleNameById[sr.role_id] = sr.name;
  const mapLabel = (r: (typeof defaultRoles)[number]) =>
    systemRoleNameById[r.system_role_mappings[0]] ?? r.name;

  const [visibility, setVisibility] = useState<Record<string, boolean>>({});
  const [defaultNames, setDefaultNames] = useState<Record<string, string>>({});
  const [customRoles, setCustomRoles] = useState<CustomRoleState[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Per-row inline validation: keyed by custom role id (custom roles) or tenant_role_id (defaults).
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [defaultRowErrors, setDefaultRowErrors] = useState<Record<string, string>>({});

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  // Stable ref so the notify effect does not re-run when the parent re-renders
  const onRolesChangeRef = useRef(onRolesChange);
  useEffect(() => { onRolesChangeRef.current = onRolesChange; });

  useEffect(() => {
    if (!hydrated) return;
    const snapshot: RolesStateSnapshot = {
      defaultRoles: defaultRoles.map((r) => ({
        name: defaultNames[r.tenant_role_id] ?? r.name,
        visible: visibility[r.tenant_role_id] ?? false,
      })),
      customRoles: customRoles.map((c) => ({
        name: c.name,
        visible: c.visibility && !!c.name.trim() && c.mappings.length > 0,
        mappings: c.mappings.map(
          (rid) =>
            defaultNames[rid] ??
            defaultRoles.find((r) => r.tenant_role_id === rid)?.name ??
            rid,
        ),
      })),
    };
    onRolesChangeRef.current?.(snapshot);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, visibility, customRoles, defaultNames, defaultRoles]);

  useEffect(() => {
    if (hydrated || !allTenantRoles.length) return;
    const vis: Record<string, boolean> = {};
    const names: Record<string, string> = {};
    for (const r of defaultRoles) {
      vis[r.tenant_role_id] = r.visibility;
      names[r.tenant_role_id] = r.name;
    }
    setVisibility(vis);
    setDefaultNames(names);
    setCustomRoles(
      allTenantRoles
        .filter((r) => !r.is_default)
        .map((tr): CustomRoleState => ({
          id: uid(),
          tenantRoleId: tr.tenant_role_id,
          name: tr.name,
          mappings: defaultRoles
            .filter((dr) => dr.system_role_mappings.some((s) => tr.system_role_mappings.includes(s)))
            .map((dr) => dr.tenant_role_id),
          dropdownOpen: false,
          visibility: tr.visibility,
        }))
    );
    setHydrated(true);
  }, [allTenantRoles, hydrated]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      // Close any open dropdown whose wrapper div does not contain the click target
      setCustomRoles((prev) =>
        prev.map((c) => {
          if (!c.dropdownOpen) return c;
          const wrapper = dropdownRefs.current.get(c.id);
          if (wrapper && !wrapper.contains(target)) {
            return { ...c, dropdownOpen: false };
          }
          return c;
        }),
      );
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useImperativeHandle(ref, (): StepHandle => ({
    getState: () => (({
      defaultRoles: defaultRoles.map((r) => ({
        name: defaultNames[r.tenant_role_id] ?? r.name,
        visible: visibility[r.tenant_role_id] ?? false,
      })),
      customRoles: customRoles.map((c) => ({
        name: c.name,
        visible: c.visibility && !!c.name.trim() && c.mappings.length > 0,
        mappings: c.mappings.map(
          (rid) =>
            defaultNames[rid] ??
            defaultRoles.find((r) => r.tenant_role_id === rid)?.name ??
            rid,
        ),
      })),
    }) as RolesStateSnapshot) as unknown as Record<string, unknown>,
    submit: persist,
    toggleRoleVisibility: (name: string, visible: boolean) => {
      const role = defaultRoles.find(
        (r) => (defaultNames[r.tenant_role_id] ?? r.name).toLowerCase() === name.toLowerCase(),
      );
      if (role) setVisibility((prev) => ({ ...prev, [role.tenant_role_id]: visible }));
    },
    addRole: (name: string, baseRoleNames: string[]) => {
      const mappingIds = baseRoleNames
        .map((n) => defaultRoles.find(
          (r) => (defaultNames[r.tenant_role_id] ?? r.name).toLowerCase() === n.toLowerCase(),
        )?.tenant_role_id)
        .filter((id): id is string => Boolean(id));
      setCustomRoles((prev) => [
        ...prev,
        { id: uid(), name, mappings: mappingIds, dropdownOpen: false, visibility: true },
      ]);
    },
    updateRoleMappings: (name: string, add: string[], remove: string[]) => {
      setCustomRoles((prev) =>
        prev.map((c) => {
          if (c.name.toLowerCase() !== name.toLowerCase()) return c;
          const addIds = add.map((n) => defaultRoles.find(
            (r) => (defaultNames[r.tenant_role_id] ?? r.name).toLowerCase() === n.toLowerCase(),
          )?.tenant_role_id).filter((id): id is string => Boolean(id));
          const removeIds = remove.map((n) => defaultRoles.find(
            (r) => (defaultNames[r.tenant_role_id] ?? r.name).toLowerCase() === n.toLowerCase(),
          )?.tenant_role_id).filter((id): id is string => Boolean(id));
          let newMappings = [...c.mappings];
          for (const id of addIds) { if (!newMappings.includes(id)) newMappings.push(id); }
          newMappings = newMappings.filter((id) => !removeIds.includes(id));
          return { ...c, mappings: newMappings };
        })
      );
    },
    removeRole: (name: string) => {
      setCustomRoles((prev) => prev.filter((c) => c.name.toLowerCase() !== name.toLowerCase()));
    },
    renameRole: (currentName: string, newName: string) => {
      // Try renaming a default role first (update its display name override).
      const matchedDefault = defaultRoles.find(
        (r) => (defaultNames[r.tenant_role_id] ?? r.name).toLowerCase() === currentName.toLowerCase(),
      );
      if (matchedDefault) {
        setDefaultNames((prev) => ({ ...prev, [matchedDefault.tenant_role_id]: newName }));
        return;
      }
      // Fall back to renaming a custom role.
      setCustomRoles((prev) =>
        prev.map((c) => c.name.toLowerCase() === currentName.toLowerCase() ? { ...c, name: newName } : c)
      );
    },
  }), [defaultRoles, defaultNames, visibility, customRoles]);

  const addCustomRole = () =>
    setCustomRoles((prev) => [...prev, { id: uid(), name: '', mappings: [], dropdownOpen: false, visibility: true }]);

  const deleteCustomRole = (id: string) => {
    dropdownRefs.current.delete(id);
    setCustomRoles((prev) => prev.filter((c) => c.id !== id));
  };

  const clearRowError = (id: string) =>
    setRowErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });

  const updateCustomName = (id: string, name: string) => {
    clearRowError(id);
    // Also clear any default-row duplicate errors — typing resolves potential conflicts globally.
    setDefaultRowErrors({});
    setCustomRoles((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        // Custom Role Name is mandatory before mapping. Clearing it auto-removes every selected
        // mapping and closes the dropdown, so no orphaned mapping can exist without a name.
        const cleared = name.trim() === '';
        return {
          ...c,
          name,
          mappings: cleared ? [] : c.mappings,
          dropdownOpen: cleared ? false : c.dropdownOpen,
        };
      }),
    );
  };
  const toggleDropdown = (id: string) =>
    setCustomRoles((prev) =>
      prev.map((c) => {
        if (c.id !== id) return { ...c, dropdownOpen: false };
        // Never open the mapping dropdown until a role name is entered.
        if (!c.name.trim()) return { ...c, dropdownOpen: false };
        return { ...c, dropdownOpen: !c.dropdownOpen };
      }),
    );
  const toggleCustomVisibility = (id: string) =>
    setCustomRoles((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        // A custom role can only be selected once it's complete: a name AND at least one mapping.
        const complete = !!c.name.trim() && c.mappings.length > 0;
        if (!complete) return c;
        return { ...c, visibility: !c.visibility };
      }),
    );
  const toggleMapping = (customId: string, defaultRoleId: string) => {
    clearRowError(customId);
    setCustomRoles((prev) =>
      prev.map((c) => {
        if (c.id !== customId) return c;
        // Guard: no mapping changes while the name is empty (the control is disabled in that state).
        if (!c.name.trim()) return c;
        const has = c.mappings.includes(defaultRoleId);
        return {
          ...c,
          mappings: has
            ? c.mappings.filter((r) => r !== defaultRoleId)
            : [...c.mappings, defaultRoleId],
          // Close the dropdown immediately after each selection; reopen manually to add more.
          dropdownOpen: false,
        };
      }),
    );
  };

  const persist = async (): Promise<SubmitResult> => {
    setError('');

    // ── Duplicate name check across ALL roles (default + custom) ─────────────
    // Collect every name that will be saved: default role display names + custom role names.
    // Compare trimmed + lowercase so "Project Manager" and "project manager" are the same.
    const defaultErrs: Record<string, string> = {};
    const customErrs: Record<string, string> = {};

    const allNames: { key: string; name: string; isDefault: boolean }[] = [
      ...defaultRoles.map((r) => ({
        key: r.tenant_role_id,
        name: (defaultNames[r.tenant_role_id] ?? r.name).trim(),
        isDefault: true,
      })),
      ...customRoles.map((c) => ({
        key: c.id,
        name: c.name.trim(),
        isDefault: false,
      })),
    ];

    // Build a frequency map; mark every key that shares a normalised name with another row.
    const seen = new Map<string, string[]>(); // normalised → keys
    for (const { key, name } of allNames) {
      if (!name) continue; // empty names caught below
      const norm = name.toLowerCase();
      seen.set(norm, [...(seen.get(norm) ?? []), key]);
    }
    let hasDuplicate = false;
    for (const keys of seen.values()) {
      if (keys.length < 2) continue;
      hasDuplicate = true;
      for (const key of keys) {
        const entry = allNames.find((e) => e.key === key)!;
        const msg = `"${entry.name}" is already used by another role. Choose a unique name.`;
        if (entry.isDefault) defaultErrs[key] = msg;
        else customErrs[key] = msg;
      }
    }
    setDefaultRowErrors(defaultErrs);

    // ── Custom role completeness check ────────────────────────────────────────
    let firstFail: SubmitResult | null = null;
    for (const c of customRoles) {
      if (!c.name.trim()) {
        customErrs[c.id] ??= 'Enter a custom role name.';
        firstFail ??= { ok: false, reason: 'validation', message: 'Enter a custom role name.' };
      } else if (c.mappings.length === 0) {
        customErrs[c.id] ??= 'Select at least one role to map.';
        firstFail ??= { ok: false, reason: 'validation', message: 'Select at least one role to map.' };
      }
    }
    setRowErrors(customErrs);

    if (hasDuplicate) {
      return { ok: false, reason: 'validation', message: 'Role names must be unique. Fix the highlighted rows.' };
    }
    if (firstFail) return firstFail;
    setSaving(true);
    try {
      for (const r of defaultRoles) {
        await updateTenantRole({
          id: r.tenant_role_id,
          data: { name: defaultNames[r.tenant_role_id] ?? r.name, visibility: visibility[r.tenant_role_id] ?? r.visibility },
        }).unwrap();
      }
      for (const c of customRoles) {
        const systemMappings = [
          ...new Set(c.mappings.flatMap(
            (tid) => defaultRoles.find((r) => r.tenant_role_id === tid)?.system_role_mappings ?? []
          )),
        ];
        if (systemMappings.length === 0) {
          const msg = `"${c.name.trim()}" must map to at least one default role.`;
          setError(msg);
          setSaving(false);
          return { ok: false, reason: 'validation', message: msg };
        }
        if (c.tenantRoleId) {
          await updateTenantRole({
            id: c.tenantRoleId,
            data: { name: c.name.trim(), system_role_mappings: systemMappings, visibility: c.visibility },
          }).unwrap();
        } else {
          await createTenantRole({
            name: c.name.trim(),
            system_role_mappings: systemMappings,
            visibility: c.visibility,
          }).unwrap();
        }
      }
      return { ok: true };
    } catch {
      setError(t('onboarding.steps.failedToSaveRoles'));
      return { ok: false, reason: 'transient', message: 'Failed to save roles.' };
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => { void persist().then((r) => { if (r.ok) onNext(); }); };

  const roleName = (rid: string) =>
    defaultNames[rid] ?? defaultRoles.find((r) => r.tenant_role_id === rid)?.name ?? rid;

  return (
    <div className={`bg-ui-card rounded-2xl ${disabled ? 'pointer-events-none opacity-60 select-none' : ''}`} ref={containerRef}>

      {/* Header */}
      <div className="px-4 pt-5 pb-4 sm:px-8 sm:pt-8 sm:pb-5">
        <h2 className="text-xl font-bold text-ui-text sm:text-2xl">Company Role</h2>
        <p className="text-sm text-ui-text2 mt-1">
          Choose your active default roles, or mix their features together to build a custom option.
        </p>
      </div>

      {isLoading ? (
        <div className="py-12 flex items-center justify-center gap-2 text-sm text-ui-text3 px-4">
          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
          Loading roles…
        </div>
      ) : (
        <>
          {/* ── Mobile layout: card list (shown below sm) ─────────────────── */}
          <div className="sm:hidden px-4 pb-2">
            <div className="divide-y divide-ui-border2">
              {/* Default roles */}
              {defaultRoles.map((r) => {
                const checked = visibility[r.tenant_role_id] ?? false;
                const nameErr = defaultRowErrors[r.tenant_role_id];
                return (
                  <div key={r.tenant_role_id} className="py-3">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={checked}
                        onClick={() => setVisibility((prev) => ({ ...prev, [r.tenant_role_id]: !prev[r.tenant_role_id] }))}
                      />
                      <div className="flex-1 min-w-0">
                        <input
                          type="text"
                          value={defaultNames[r.tenant_role_id] ?? r.name}
                          onChange={(e) => {
                            setDefaultNames((prev) => ({ ...prev, [r.tenant_role_id]: e.target.value }));
                            setDefaultRowErrors((prev) => { const n = { ...prev }; delete n[r.tenant_role_id]; return n; });
                          }}
                          placeholder={r.name}
                          className="w-full border rounded-lg px-3 py-2 text-sm text-ui-text placeholder-ui-text3 focus:outline-none transition-colors"
                          style={{
                            borderColor: nameErr ? '#EF4444' : checked ? 'rgb(var(--color-brand-dark))' : '#E5E7EB',
                            backgroundColor: 'rgb(var(--ui-input))',
                          }}
                        />
                      </div>
                      <span className="flex-shrink-0 text-xs text-ui-text3 bg-ui-subtle px-2 py-1 rounded-full">Default</span>
                    </div>
                    {nameErr && <p className="text-red-500 text-xs mt-1 pl-8">{nameErr}</p>}
                  </div>
                );
              })}

              {/* Custom roles */}
              {customRoles.map((c) => (
                <div key={c.id} className="py-3 space-y-2">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={c.visibility && !!c.name.trim() && c.mappings.length > 0}
                      disabled={!c.name.trim() || c.mappings.length === 0}
                      onClick={() => toggleCustomVisibility(c.id)}
                    />
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Custom role name…"
                        value={c.name}
                        onChange={(e) => updateCustomName(c.id, e.target.value)}
                        className="flex-1 min-w-0 border border-brand-dark rounded-lg px-3 py-2 text-sm text-ui-text placeholder-ui-text3 focus:outline-none focus:ring-2 focus:ring-brand-dark/20"
                      />
                      <button
                        type="button"
                        onClick={() => deleteCustomRole(c.id)}
                        className="flex-shrink-0 text-red-400 hover:text-red-600 p-1"
                      >
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    <span className="flex-shrink-0 text-xs text-ui-text3 bg-ui-subtle px-2 py-1 rounded-full">Custom</span>
                  </div>
                  <div className="flex flex-wrap gap-1 pl-8">
                    {c.mappings.length === 0 ? (
                      <span className="text-xs text-ui-text3">No roles mapped</span>
                    ) : (
                      c.mappings.map((rid) => (
                        <span key={rid} className="text-xs bg-brand-light text-brand-dark px-2 py-0.5 rounded">
                          {roleName(rid)}
                        </span>
                      ))
                    )}
                  </div>
                  {rowErrors[c.id] && (
                    <p className="text-red-500 text-xs pl-8">{rowErrors[c.id]}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* ── Desktop layout: 4-column table (shown at sm+) ─────────────── */}
          <div className="hidden sm:block px-8">
            {/* Column headers */}
            <div className="grid items-center border-b border-ui-border pb-2" style={{ gridTemplateColumns: '56px 1fr 220px 90px' }}>
              <span className="text-[11px] font-semibold text-ui-text3 uppercase tracking-widest">Select</span>
              <span className="text-[11px] font-semibold text-ui-text3 uppercase tracking-widest">Role Name / Profile Name</span>
              <span className="text-[11px] font-semibold text-ui-text3 uppercase tracking-widest">Map</span>
              <span className="text-[11px] font-semibold text-ui-text3 uppercase tracking-widest">Type</span>
            </div>

            <div className="divide-y divide-ui-border2">
              {/* Default role rows */}
              {defaultRoles.map((r) => {
                const checked = visibility[r.tenant_role_id] ?? false;
                const nameErr = defaultRowErrors[r.tenant_role_id];
                return (
                  <div key={r.tenant_role_id}>
                    <div className="grid items-center py-4" style={{ gridTemplateColumns: '56px 1fr 220px 90px' }}>
                      <Checkbox
                        checked={checked}
                        onClick={() => setVisibility((prev) => ({ ...prev, [r.tenant_role_id]: !prev[r.tenant_role_id] }))}
                      />
                      <div className="pr-4">
                        <input
                          type="text"
                          value={defaultNames[r.tenant_role_id] ?? r.name}
                          onChange={(e) => {
                            setDefaultNames((prev) => ({ ...prev, [r.tenant_role_id]: e.target.value }));
                            setDefaultRowErrors((prev) => { const n = { ...prev }; delete n[r.tenant_role_id]; return n; });
                          }}
                          placeholder={r.name}
                          className="w-full border rounded-lg px-3 py-2 text-sm text-ui-text placeholder-ui-text3 focus:outline-none transition-colors"
                          style={{
                            borderColor: nameErr ? '#EF4444' : checked ? 'rgb(var(--color-brand-dark))' : '#E5E7EB',
                            backgroundColor: 'rgb(var(--ui-input))',
                          }}
                        />
                      </div>
                      <div className="pr-4">
                        {/* MAP shows the fixed system role this row is wired to (e.g. "Tenant Admin").
                            Renaming the Profile Name (left column) never changes this — it always
                            reflects the underlying system_role_mappings, not the display name. */}
                        <span className="text-sm text-ui-text">{mapLabel(r)}</span>
                      </div>
                      <span className="text-sm text-ui-text2">Default</span>
                    </div>
                    {nameErr && (
                      <p className="text-red-500 text-xs pb-2" style={{ paddingLeft: '56px' }}>{nameErr}</p>
                    )}
                  </div>
                );
              })}

              {/* Custom role rows */}
              {customRoles.map((c) => {
                const nameEmpty = !c.name.trim();
                // A custom role is only "selected" when complete: name + at least one mapped role.
                const complete = !nameEmpty && c.mappings.length > 0;
                return (
                <div key={c.id}>
                  <div className="grid items-center py-4" style={{ gridTemplateColumns: '56px 1fr 220px 90px' }}>
                    <Checkbox checked={c.visibility && complete} disabled={!complete} onClick={() => toggleCustomVisibility(c.id)} />
                    <div className="pr-4 flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Custom role name…"
                        value={c.name}
                        onChange={(e) => updateCustomName(c.id, e.target.value)}
                        className="flex-1 border border-brand-dark rounded-lg px-3 py-2 text-sm text-ui-text placeholder-ui-text3 focus:outline-none focus:ring-2 focus:ring-brand-dark/20"
                      />
                      <button type="button" onClick={() => deleteCustomRole(c.id)} title="Remove" className="flex-shrink-0 text-red-400 hover:text-red-600 p-0.5">
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    <div className="pr-4 relative">
                      <button
                        type="button"
                        onClick={() => toggleDropdown(c.id)}
                        disabled={nameEmpty}
                        aria-disabled={nameEmpty}
                        title={nameEmpty ? 'Enter role name first' : undefined}
                        className={`w-full border rounded-lg px-2 py-1.5 text-sm text-left flex items-center gap-1.5 min-h-[38px] flex-wrap focus:outline-none focus:ring-2 focus:ring-brand-dark/20 ${nameEmpty ? 'bg-ui-input border-ui-border cursor-not-allowed' : 'bg-ui-card border-ui-border hover:border-brand-dark/50'}`}
                      >
                        <div className="flex flex-wrap gap-1 flex-1">
                          {nameEmpty ? (
                            <span className="text-ui-text3 text-xs">Enter role name first</span>
                          ) : c.mappings.length === 0 ? (
                            <span className="text-ui-text3 text-xs">Select roles…</span>
                          ) : (
                            c.mappings.map((rid) => (
                              <span key={rid} className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-brand-light text-brand-dark text-xs font-medium rounded">
                                {roleName(rid)}
                                <button type="button" onClick={(e) => { e.stopPropagation(); toggleMapping(c.id, rid); }} className="ml-0.5 hover:opacity-70">
                                  <svg viewBox="0 0 12 12" fill="currentColor" className="w-2.5 h-2.5">
                                    <path d="M8.485 3.515a.75.75 0 00-1.06 0L6 4.94 4.575 3.515a.75.75 0 10-1.06 1.06L4.94 6 3.515 7.425a.75.75 0 101.06 1.06L6 7.06l1.425 1.425a.75.75 0 101.06-1.06L7.06 6l1.425-1.425a.75.75 0 000-1.06z" />
                                  </svg>
                                </button>
                              </span>
                            ))
                          )}
                        </div>
                        <svg viewBox="0 0 20 20" fill="currentColor" className={`w-4 h-4 flex-shrink-0 transition-transform text-ui-text3 ${c.dropdownOpen ? 'rotate-180' : ''}`}>
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                      {c.dropdownOpen && !nameEmpty && (
                        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-ui-card border border-ui-border rounded-xl shadow-lg overflow-y-auto max-h-48">
                          {defaultRoles.map((r) => (
                            <label key={r.tenant_role_id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-brand-light/40 cursor-pointer">
                              <input type="checkbox" checked={c.mappings.includes(r.tenant_role_id)} onChange={() => toggleMapping(c.id, r.tenant_role_id)} className="w-4 h-4 rounded accent-brand-dark flex-shrink-0" />
                              <span className="text-sm text-ui-text flex-1">{defaultNames[r.tenant_role_id] ?? r.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <span className="text-sm text-ui-text2">Custom</span>
                  </div>
                  {rowErrors[c.id] && (
                    <p className="text-red-500 text-xs pb-3" style={{ paddingLeft: '56px' }}>{rowErrors[c.id]}</p>
                  )}
                </div>
                );
              })}
            </div>
          </div>

          {/* Add Custom Role — shared */}
          <div className="py-4 border-t border-ui-border2 px-4 sm:px-8">
            <button
              type="button"
              onClick={addCustomRole}
              className="border-2 border-dashed border-[#C7B4E8] dark:border-brand-dark/40 rounded-xl px-4 py-2 flex items-center gap-2 text-sm font-medium text-brand-dark hover:border-brand-bright hover:bg-brand-light/20 transition-colors"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Add Custom Role
            </button>
          </div>
        </>
      )}

      {/* Footer */}
      <div className="border-t border-ui-border px-4 pt-4 pb-6 sm:px-8 sm:pt-6 sm:pb-8">
        {error && <p className="text-red-500 text-xs mb-3">{error}</p>}
        <div className="flex justify-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 sm:flex-initial sm:px-10 py-3 border border-ui-border text-ui-text rounded-xl text-sm font-medium hover:bg-ui-hover transition-colors flex items-center justify-center gap-2"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 sm:flex-initial sm:px-10 py-3 bg-[#1c1849] text-white rounded-xl text-sm font-medium hover:bg-[#2d2b6b] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                Saving…
              </>
            ) : (
              <>
                Next
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </>
            )}
          </button>
        </div>
        {onSkip && (
          <div className="text-center mt-3">
            <button
              type="button"
              onClick={onSkip}
              className="text-sm text-ui-text3 hover:text-ui-text2 transition-colors"
            >
              Skip for now
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

TeamRolesStep.displayName = 'TeamRolesStep';

export default TeamRolesStep;
